import { type ApiInvoice, projectApiInvoice } from '@/features/faturas';
import type { Fatura } from '@/features/faturas/types';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import type {
  AntecipacaoPagamentoFormData,
  AntecipacaoPagamentoResultado,
  ConsumoEmAberto,
  Estorno,
  PainelFinanceiroResumo,
} from '../types';
import {
  type ApiPayableItem,
  type ApiRefund,
  type ApiWorkspaceBillingPanel,
  projectPayableItem,
  projectRefund,
  projectWorkspaceBillingPanel,
} from './apiTypes';

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.error?.message ?? fallbackMessage);
  }
  if (json == null || json.data === undefined) {
    throw new Error(fallbackMessage);
  }
  return json.data as T;
}

export const financeiroEscritorioService = {
  async fetchResumo(): Promise<PainelFinanceiroResumo> {
    const response = await apiGet('/billing/panel');
    const data = await readJson<ApiWorkspaceBillingPanel>(
      response,
      'Falha ao carregar painel financeiro',
    );
    return projectWorkspaceBillingPanel(data);
  },

  async fetchFaturas(): Promise<Fatura[]> {
    const response = await apiGet('/billing/workspace');
    const data = await readJson<{ items: ApiInvoice[] }>(response, 'Falha ao listar faturas');
    return data.items.map(projectApiInvoice);
  },

  async fetchFaturaById(faturaId: string): Promise<Fatura> {
    const response = await apiGet(`/billing/workspace/${faturaId}`);
    const data = await readJson<ApiInvoice>(response, 'Fatura não encontrada');
    return projectApiInvoice(data);
  },

  async fetchConsumoEmAberto(): Promise<ConsumoEmAberto[]> {
    const response = await apiGet('/billing/workspace/payables');
    const data = await readJson<{ items: ApiPayableItem[] }>(
      response,
      'Falha ao carregar itens em aberto',
    );
    return data.items.map(projectPayableItem);
  },

  async gerarFaturaAntecipada(
    data: AntecipacaoPagamentoFormData,
  ): Promise<AntecipacaoPagamentoResultado> {
    const response = await apiPost('/billing/workspace/payables/anticipate', {
      eventIds: data.itensIds,
      grouping: data.modoAgrupamento,
    });
    const result = await readJson<{ invoices: ApiInvoice[] }>(
      response,
      'Falha ao gerar faturas antecipadas',
    );
    return {
      faturasGeradas: result.invoices.map(projectApiInvoice),
      itensFaturados: data.itensIds,
    };
  },

  async fetchEstornos(): Promise<Estorno[]> {
    const response = await apiGet('/billing/workspace/refunds');
    const data = await readJson<{ items: ApiRefund[] }>(response, 'Falha ao carregar estornos');
    return data.items.map(projectRefund);
  },
};
